import React from "react";
import {
    ContentSection,
    GridRow,
    GridColumn,
    LabeledText,
    LabeledLink,
    ButtonRenderProps,
    LabeledDate,
    LabeledDateTime,
    LabeledBadge,
    BadgeColor
} from "sprint-common-web";

import { LabeledRecordTypeBadge } from "../../../../common/components/RecordTypeBadge";
import { LabeledTrailerInfoBadge } from "../../../../common/components/trailer-info/LabeledTrailerInfoBadge";
import { getFileRecordDetailsHref } from "../../../../content-ingestion/ContentIngestionRoutes";
import { SprintAccessRight } from "../../../../core/security/SprintAccessRight";
import { useAccessVerifier } from "../../../../core/security/useAccessVerifier";
import { useOnPremAssetDetails } from "../common/hooks/useOnPremAssetDetails";
import { OnPremFileRecordReference } from "../OnPremAssetDetails";
import {
    EditOnPremAssetLibraryAside,
    EditOnPremAssetLibraryAsideProps
} from "./edit/EditOnPremAssetLibraryButtonAside";
import { AssetStatusButtonProps, OnPremAssetStatusButton } from "./OnPremAssetStatusButton";

export const OnPremLibrarySection = (): JSX.Element => {
    const { isAuthorized } = useAccessVerifier();

    const { data: assetDetails, isLoading } = useOnPremAssetDetails();

    const statusButton: ButtonRenderProps<AssetStatusButtonProps> = {
        render: OnPremAssetStatusButton,
        disabled: !isAuthorized([SprintAccessRight.AssetsReadWrite])
    };

    const editButton: ButtonRenderProps<EditOnPremAssetLibraryAsideProps> = {
        isLabeled: true,
        render: EditOnPremAssetLibraryAside
    };

    const InfoView = assetDetails?.isTrailer && <LabeledTrailerInfoBadge />;

    return (
        <ContentSection
            header="Library"
            buttons={[statusButton, editButton]}
            isLoading={isLoading}
            style={{ height: 400 }}
        >
            <GridRow>
                <GridColumn sm={6}>
                    <LabeledBadge
                        label="Asset type"
                        value="On-Prem"
                        testId="record-type-field"
                        color={BadgeColor.Blue}
                        badgeSize={14}
                    />
                    <LabeledRecordTypeBadge value={assetDetails?.type} />
                    {InfoView}
                    <LabeledText
                        label="File path"
                        value={assetDetails?.fileRecordReference?.filePath}
                        maxLength={40}
                        testId="file-path-field"
                        textTooltip
                    />
                    <LabeledText
                        label="Delivery file name"
                        value={getDeliveryFileName(assetDetails?.fileRecordReference)}
                        maxLength={40}
                        testId="delivery-file-name-field"
                        textTooltip
                    />
                </GridColumn>
                <GridColumn sm={6}>
                    {assetDetails?.fileRecordReference && (
                        <LabeledLink
                            label="File record"
                            value="Go to file record details"
                            maxLength={40}
                            testId="file-record-link"
                            href={getFileRecordDetailsHref(assetDetails.fileRecordReference?.id)}
                            underlineHref={true}
                        />
                    )}
                    <LabeledText label="Originator" value={assetDetails?.originator} testId="originator-field" />

                    <LabeledDate
                        label="Arrival date"
                        value={assetDetails?.fileRecordReference?.arrivalDate}
                        testId="arrival-date"
                    />
                    <LabeledDateTime
                        label="Ingested date"
                        value={assetDetails?.fileRecordReference?.ingestedDate}
                        testId="ingested-date"
                    />
                </GridColumn>
            </GridRow>
        </ContentSection>
    );
};

const getDeliveryFileName = (fileRef?: OnPremFileRecordReference) => `${fileRef?.fileName}.${fileRef?.fileExtension}`;
